import React, { useState, useEffect, useMemo } from 'react';
import { useBrand } from '../../context/BrandContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Stack
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import {
  getInventoryBatches,
  getWarehouses,
  getExternalStockLevels,
  getExternalWarehouses,
  formatNumber,
  formatCurrency
} from '../../services/inventoryService';

function WarehouseBreakdown() {
  const { selectedBrand } = useBrand();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batches, setBatches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [externalStock, setExternalStock] = useState([]);
  const [externalWarehouses, setExternalWarehouses] = useState([]);
  const [expandedWarehouse, setExpandedWarehouse] = useState(null);

  useEffect(() => {
    if (selectedBrand?.id) {
      loadData();
    }
  }, [selectedBrand?.id]);

  const loadData = async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const [
        batchesRes, 
        warehousesRes,
        externalStockRes,
        externalWarehousesRes
      ] = await Promise.all([
        getInventoryBatches({ brandId: selectedBrand.id, limit: 1000 }),
        getWarehouses({ is_active: true }),
        getExternalStockLevels(selectedBrand.id, { limit: 1000 }).catch(err => {
          console.warn('Failed to load external stock:', err);
          return { data: { stock: [] } };
        }),
        getExternalWarehouses(selectedBrand.id).catch(err => {
          console.warn('Failed to load external warehouses:', err);
          return { data: { warehouses: [] } };
        })
      ]);
      
      setBatches(batchesRes?.data?.batches || []);
      setWarehouses(warehousesRes?.data || []);
      setExternalStock(externalStockRes?.data?.stock || []);
      setExternalWarehouses(externalWarehousesRes?.data?.warehouses || []);
    } catch (err) {
      console.error('Failed to load warehouse data:', err);
      setError('Failed to load warehouse data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Group inventory by warehouse
  const warehouseBreakdown = useMemo(() => {
    const warehouseMap = {};
    
    // Process internal inventory
    batches.forEach(batch => {
      const warehouseCode = batch.warehouse_code;
      if (!warehouseMap[warehouseCode]) {
        const warehouse = warehouses.find(w => w.warehouse_code === warehouseCode);
        warehouseMap[warehouseCode] = {
          warehouse_code: warehouseCode,
          warehouse_name: warehouse?.warehouse_name || warehouseCode,
          warehouse_type: 'internal',
          total_quantity: 0,
          total_value: 0,
          sku_count: new Set(),
          items: []
        };
      }
      
      const qty = parseFloat(batch.current_quantity) || 0;
      const cost = parseFloat(batch.unit_cost) || 0;
      const value = qty * cost;
      
      warehouseMap[warehouseCode].total_quantity += qty;
      warehouseMap[warehouseCode].total_value += value;
      warehouseMap[warehouseCode].sku_count.add(batch.sku);
      warehouseMap[warehouseCode].items.push({
        sku: batch.sku,
        product_name: batch.product_name || batch.sku,
        batch_number: batch.batch_number,
        quantity: qty,
        unit_cost: cost,
        value: value,
        expiry_date: batch.expiry_date
      });
    });
    
    // Process external inventory (Amazon FBA, etc.)
    externalStock.forEach(stock => {
      const warehouseCode = stock.warehouse_location || stock.warehouse_code;
      if (!warehouseMap[warehouseCode]) {
        const warehouse = externalWarehouses.find(w => w.warehouse_code === warehouseCode || w.warehouse_location === warehouseCode);
        warehouseMap[warehouseCode] = {
          warehouse_code: warehouseCode,
          warehouse_name: warehouse?.warehouse_name || warehouseCode,
          warehouse_type: 'external',
          warehouse_subtype: warehouse?.warehouse_type || 'external',
          total_quantity: 0,
          total_value: 0,
          total_recommended_qty: 0,
          sku_count: new Set(),
          items: []
        };
      }

      const qty = parseFloat(stock.quantity) || 0;
      const cost = parseFloat(stock.cogs) || 0;
      const value = qty * cost;
      const recommendedQty = parseInt(stock.recommended_replenishment_qty) || 0;

      warehouseMap[warehouseCode].total_quantity += qty;
      warehouseMap[warehouseCode].total_value += value;
      warehouseMap[warehouseCode].total_recommended_qty += recommendedQty;
      warehouseMap[warehouseCode].sku_count.add(stock.sku);
      warehouseMap[warehouseCode].items.push({
        sku: stock.sku,
        product_name: stock.product_name || stock.sku,
        quantity: qty,
        unit_cost: cost,
        value: value,
        last_updated: stock.last_updated,
        // Amazon restock recommendations
        recommended_replenishment_qty: recommendedQty,
        recommended_ship_date: stock.recommended_ship_date,
        days_of_supply: stock.days_of_supply_at_amazon,
        restock_alert: stock.restock_alert,
        sales_last_30_days: stock.sales_last_30_days,
        units_sold_last_30_days: stock.units_sold_last_30_days
      });
    });
    
    // Convert to array and sort by total value (descending)
    return Object.values(warehouseMap)
      .map(wh => ({
        ...wh,
        sku_count: wh.sku_count.size
      }))
      .sort((a, b) => b.total_value - a.total_value);
  }, [batches, externalStock, warehouses, externalWarehouses]);

  const toggleWarehouse = (warehouseCode) => {
    setExpandedWarehouse(expandedWarehouse === warehouseCode ? null : warehouseCode);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <WarehouseIcon color="primary" />
                <Typography variant="h6">{warehouseBreakdown.length}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Total Warehouses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <InventoryIcon color="success" />
                <Typography variant="h6">
                  {formatNumber(warehouseBreakdown.reduce((sum, wh) => sum + wh.total_quantity, 0))}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Total Units
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BusinessIcon color="warning" />
                <Typography variant="h6">
                  {warehouseBreakdown.filter(wh => wh.warehouse_type === 'internal').length}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Internal Warehouses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BusinessIcon color="info" />
                <Typography variant="h6">
                  {warehouseBreakdown.filter(wh => wh.warehouse_type === 'external').length}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                External Warehouses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Warehouse List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Warehouse Breakdown
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>Warehouse</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">SKUs</TableCell>
                  <TableCell align="right">Total Units</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouseBreakdown.map((warehouse) => (
                  <React.Fragment key={warehouse.warehouse_code}>
                    {/* Warehouse Summary Row */}
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleWarehouse(warehouse.warehouse_code)}
                        >
                          {expandedWarehouse === warehouse.warehouse_code ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {warehouse.warehouse_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {warehouse.warehouse_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={warehouse.warehouse_type === 'internal' ? 'Internal' : 
                                warehouse.warehouse_subtype === 'amazon_fba' ? 'Amazon FBA' : 'External'}
                          size="small"
                          color={warehouse.warehouse_type === 'internal' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {warehouse.sku_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatNumber(warehouse.total_quantity)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(warehouse.total_value)}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details Row */}
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedWarehouse === warehouse.warehouse_code ? undefined : 0 }}>
                        <Collapse in={expandedWarehouse === warehouse.warehouse_code} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Inventory Details
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>SKU</TableCell>
                                  <TableCell>Product Name</TableCell>
                                  {warehouse.warehouse_type === 'internal' && <TableCell>Batch</TableCell>}
                                  <TableCell align="right">Quantity</TableCell>
                                  {warehouse.warehouse_subtype === 'amazon_fba' && (
                                    <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                                      Amazon Rec. Qty
                                    </TableCell>
                                  )}
                                  {warehouse.warehouse_subtype === 'amazon_fba' && (
                                    <TableCell align="right">Days of Supply</TableCell>
                                  )}
                                  <TableCell align="right">Unit Cost</TableCell>
                                  <TableCell align="right">Value</TableCell>
                                  {warehouse.warehouse_type === 'internal' && <TableCell>Expiry</TableCell>}
                                  {warehouse.warehouse_type === 'external' && <TableCell>Last Updated</TableCell>}
                                  {warehouse.warehouse_subtype === 'amazon_fba' && <TableCell>Alert</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {warehouse.items.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{item.sku}</TableCell>
                                    <TableCell>{item.product_name}</TableCell>
                                    {warehouse.warehouse_type === 'internal' && <TableCell>{item.batch_number}</TableCell>}
                                    <TableCell align="right">{formatNumber(item.quantity)}</TableCell>
                                    {warehouse.warehouse_subtype === 'amazon_fba' && (
                                      <TableCell align="right" sx={{ fontWeight: 'medium', color: item.recommended_replenishment_qty > 0 ? 'warning.main' : 'text.secondary' }}>
                                        {item.recommended_replenishment_qty > 0 ? formatNumber(item.recommended_replenishment_qty) : '-'}
                                      </TableCell>
                                    )}
                                    {warehouse.warehouse_subtype === 'amazon_fba' && (
                                      <TableCell align="right">
                                        {item.days_of_supply != null ? `${item.days_of_supply}d` : '-'}
                                      </TableCell>
                                    )}
                                    <TableCell align="right">{formatCurrency(item.unit_cost)}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.value)}</TableCell>
                                    {warehouse.warehouse_type === 'internal' && (
                                      <TableCell>{item.expiry_date || 'N/A'}</TableCell>
                                    )}
                                    {warehouse.warehouse_type === 'external' && (
                                      <TableCell>
                                        {item.last_updated ? new Date(item.last_updated).toLocaleDateString() : 'N/A'}
                                      </TableCell>
                                    )}
                                    {warehouse.warehouse_subtype === 'amazon_fba' && (
                                      <TableCell>
                                        {item.restock_alert ? (
                                          <Chip
                                            label={item.restock_alert}
                                            size="small"
                                            color={item.restock_alert.toLowerCase().includes('out') ? 'error' :
                                                   item.restock_alert.toLowerCase().includes('low') ? 'warning' : 'default'}
                                          />
                                        ) : '-'}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default WarehouseBreakdown;
